import { Link, useSearchParams } from "react-router";
import { Alert, Tabs } from "@repo/components";
import { useFlag } from "@repo/flags";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../../shell/VenueContext";
import { useOnboardingProgress } from "../../shell/useOnboardingProgress";
import { EmbedSettings } from "../embed/EmbedSettings";
import { FirstBookingPanel } from "../first-booking/FirstBookingPanel";
import { GoogleBusinessCard } from "../google-business/GoogleBusinessCard";
import { GoogleBusinessComingSoon } from "../google-business/GoogleBusinessComingSoon";
import { MetaReserveSettings } from "../meta-reserve/MetaReserveSettings";
import { PlaceMatchPage } from "../place-match/PlaceMatchPage";

/**
 * Canaux de réservation — one surface for every place a guest can book, replacing three near-adjacent
 * config screens. The default tab is what to do first; each channel is a tab over the EXISTING feature
 * component:
 *   - Partager (link, QR code, guided steps)   → FirstBookingPanel
 *   - Site web (own website, channel EMBED)    → EmbedSettings
 *   - Réseaux sociaux (Instagram/Facebook, META) → MetaReserveSettings
 *   - Google (Reserve with Google)              → PlaceMatchPage  (previously unreachable from the nav)
 * The active tab is in the URL (`?tab=share|widget|social|google`) so it is deep-linkable and the old
 * `settings/embed` / `settings/meta-reserve` routes can redirect straight to their tab.
 */
const TABS = ["share", "widget", "social", "google"] as const;
type ChannelTab = (typeof TABS)[number];

export function BookingChannelsPage() {
  const { t } = useTranslation();
  const { venueId } = useVenue();
  const { isLive } = useOnboardingProgress();
  // Dark-launch gate for Reserve with Google — see GoogleBusinessComingSoon.tsx for why. Same
  // PostHog flag key the backend checks; defaults to the "coming soon" placeholder (dark).
  const googleBusinessEnabled = useFlag("google_business.enabled", false);
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: ChannelTab = (TABS as readonly string[]).includes(raw ?? "")
    ? (raw as ChannelTab)
    : "share";

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "6" })}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <h2 className={css({ fontSize: "lg", fontWeight: "semibold", color: "fg.default" })}>
          {t("shell.nav.channels")}
        </h2>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("channels.intro")}</p>
      </div>
      {!isLive && (
        <Alert.Root>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{t("channels.notLive.title")}</Alert.Title>
            <Alert.Description>
              {t("channels.notLive.body")}{" "}
              <Link
                to={`/v/${venueId}/onboarding`}
                className={css({
                  colorPalette: "brand",
                  color: "colorPalette.text",
                  fontWeight: "medium",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                })}
              >
                {t("channels.notLive.cta")}
              </Link>
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}
      <Tabs.Root
        value={tab}
        onValueChange={(details: { value: string }) =>
          setParams({ tab: details.value }, { replace: true })
        }
      >
        <Tabs.List>
          <Tabs.Trigger value="share">{t("channels.tab.share")}</Tabs.Trigger>
          <Tabs.Trigger value="widget">{t("channels.tab.widget")}</Tabs.Trigger>
          <Tabs.Trigger value="social">{t("channels.tab.social")}</Tabs.Trigger>
          <Tabs.Trigger value="google">{t("channels.tab.google")}</Tabs.Trigger>
          <Tabs.Indicator />
        </Tabs.List>
        <Tabs.Content value="share">
          <FirstBookingPanel />
        </Tabs.Content>
        <Tabs.Content value="widget">
          <EmbedSettings />
        </Tabs.Content>
        <Tabs.Content value="social">
          <MetaReserveSettings />
        </Tabs.Content>
        <Tabs.Content value="google">
          <PlaceMatchPage />
          {googleBusinessEnabled ? <GoogleBusinessCard /> : <GoogleBusinessComingSoon />}
        </Tabs.Content>
      </Tabs.Root>
    </section>
  );
}
